<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useUserStore, useCommonStore, useQuoteStore } from '@/shared/store'
  import { type Plan, FileCategory, WordingDocName } from '@/shared/types/api.types'
  import { useI18n } from 'vue-i18n'
  import { LOAD_EVENT_NAME, PROCEED_EVENT_NAME } from './FlowSelectPlanInboundWidgetQC.constants'
  import { InboundFlowRoutes } from '@/shared/types/router.types'
  import {
    useDiscount,
    useGoogleAnalytics,
    usePerformanceTiming,
    useLinkTarget,
  } from '@/shared/composables'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'
  import { formatDateDefault, toFixedNumber } from '@/shared/utils'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import AccordionQC from '@/shared/components/AccordionQC/AccordionQC.vue'

  interface Props {
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void

    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
    (e: 'back'): void
  }

  withDefaults(defineProps<Props>(), {
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  const { push } = useRouter()

  const { getFormData } = useFormDataWithRedirect()

  const { t, locale } = useI18n()
  const { getLinkTarget } = useLinkTarget()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const quoteStore = useQuoteStore()
  const policyWording = ref('')
  const departureDate = ref<Date | null>(userStore.departureDate)
  const returnDate = ref<Date | null>(userStore.returnDate)
  const adultsCount = computed(() => userStore.adultsCount)
  const childrenCount = computed(() => userStore.childrenCount)
  const travelersCount = adultsCount.value + childrenCount.value
  const plan = ref<Plan | null>(null)
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const discountScheme = computed(() => ({
    discounts: plan.value?.discounts ?? [],
    price: plan.value?.price ?? 0,
  }))

  const { discount } = useDiscount(discountScheme)

  const quotePriceWithoutDiscount = computed(() => toFixedNumber(plan.value?.price ?? 0 + 100))

  const informationCardOptions = computed(() => [
    {
      id: 'date',
      title: t('inbound.plan.policy_information.period'),
      content: `${formatDateDefault(departureDate.value, locale.value as Locale)} - ${formatDateDefault(
        returnDate.value,
        locale.value as Locale
      )}`,
    },
    {
      id: 'travelers',
      title: t('inbound.plan.policy_information.travellers'),
      content: `${t(
        'outbound.plans.policy_information.people',
        { personCount: travelersCount },
        travelersCount
      )}`,
    },
  ])

  const onProceed = () => {
    if (plan.value) {
      userStore.setSelectedPlan(plan.value.schemecode, true)
    }
    gaService.sendSelectPlanProceed(
      plan.value?.price ?? 0,
      travelersCount,
      departureDate.value as Date,
      returnDate.value as Date
    )

    emit(PROCEED_EVENT_NAME)
  }

  const fetchQatarWordings = () =>
    commonStore
      .getQatarWordings(FileCategory.TRAVEL, WordingDocName.POLICY_WORDING_INBOUND)
      .then((data) => {
        policyWording.value = data[0]?.Link || ''
      })

  onMounted(async () => {
    await emit(LOAD_EVENT_NAME, async () => {
      if (!userStore.residenceCountry || !Object.keys(userStore.residenceCountry).length) {
        await push({ name: InboundFlowRoutes.RESIDENCE })
        return
      }

      if (!(await getFormData(TravelDirection.INBOUND, false))) return
      await Promise.all([getQuote(), fetchQatarWordings()])

      onPerformanceCheckEnd()
      gaService.sendViewPage(TravelDirection.INBOUND, AnalyticsPageName.SELECT_PLAN)
    })
  })

  const onPolicyWordingClick = () => {
    gaService.sendSelectPlanPolicyWordings(plan.value?.price ?? 0)
  }

  const planAccordionItems = computed(() => [
    {
      title: t('outbound.select_plan.insurance_values.category_2.label'),
      content: t('outbound.select_plan.insurance_values.category_2.quote_desc'),
    },
    {
      title: t('outbound.select_plan.insurance_values.category_3.label'),
      content: t('outbound.select_plan.insurance_values.category_3.quote_desc'),
    },
    {
      title: t('outbound.select_plan.insurance_values.category_1.label'),
      content: t('outbound.select_plan.insurance_values.category_1.quote_desc'),
    },
  ])

  const getQuote = async (): Promise<void> => {
    const quote = await quoteStore.getTravelQuoteUae(userStore.promocode ?? '', 0)
    if (quote.schemes.length === 0) {
      return Promise.reject('quote.schemes.length === 0')
    } else {
      plan.value = quote.schemes[0]
      return Promise.resolve()
    }
  }

  const sendCoverToggle = (planName: string, planPrice: number, coverName: string) => {
    gaService.sendCoverToggle({
      category: TravelDirection.INBOUND,
      numOfTravelers: travelersCount,
      section: 'inclusive_cover',
      planName,
      planPrice,
      coverName,
    })
  }
</script>

<template>
  <FlowCardQC
    v-show="plan"
    :title="t('inbound_travel_insurance_header')"
    class="view-plan"
    with-button
    :with-back-button="withBackButton"
    @back="emit('back')"
    @button-click="onProceed"
  >
    <WrapperQC direction="column" gap="var(--spacing-6)">
      <WrapperQC
        v-if="informationCardOptions.length > 0"
        class="view-plan__info-card"
        direction="column"
        gap="var(--spacing-4)"
      >
        <h6 class="heading-6">{{ $t('inbound.plan.policy_information.header_new') }}</h6>

        <CellQC
          v-for="informationCardOption in informationCardOptions"
          :key="informationCardOption.id"
          padding-block="spacing-0"
          :title="informationCardOption.title"
          :value="informationCardOption.content"
        />
      </WrapperQC>

      <WrapperQC v-if="plan" direction="column" gap="var(--spacing-3)" class="view-plan__plan-card">
        <div v-if="discount.percentage" class="view-plan__plan-card-discount text-small-medium">
          <span class="ltr">{{ `-${discount.percentage}%` }}</span>
        </div>

        <CellQC padding-block="spacing-0" title-color="primary" class="view-plan__plan-card-header">
          <template #title>
            <p class="paragraph-default-bold">
              {{ $t('travel.common.quote.inclusive_cover.name') }}
            </p>
          </template>

          <template #value>
            <p class="paragraph-default-bold">
              <span v-if="discount.amount > 0" class="text-small-medium view-plan__discount-price">
                {{ $t('qar_price', { price: quotePriceWithoutDiscount }) }}
              </span>
              {{ $t('qar_price', { price: plan.price }) }}
            </p>
          </template>
        </CellQC>

        <WrapperQC direction="column">
          <AccordionQC
            v-for="item in planAccordionItems"
            :key="item.title"
            :data-test="`accordion-${item.title}`"
            header-gap="var(--spacing-5)"
            :padding="{
              block: 'var(--spacing-6)',
            }"
            gap="var(--spacing-2)"
            class="view-plan__plan-card-accordion"
            icon-color="var(--colorIcnSrfPrimary)"
            @toggle="
              (isOpen) => isOpen && plan && sendCoverToggle(plan.name, plan.price, item.title)
            "
          >
            <template #header>
              <p class="text-medium">
                {{ item.title }}
              </p>
            </template>
            <template #content>
              <p class="text-regular view-plan__plan-card-content">
                {{ item.content }}
              </p>
            </template>
          </AccordionQC>
        </WrapperQC>
      </WrapperQC>
    </WrapperQC>

    <template #button>
      <WrapperQC direction="column" gap="var(--spacing-4)">
        <ButtonQC
          wide
          :href="policyWording"
          :target="getLinkTarget()"
          rel="noopener noreferrer"
          variant="ghost"
          size="medium"
          data-test="view-plan-policy-wording-link"
          @click="onPolicyWordingClick"
        >
          {{ $t('outbound.plans.plan_information.policy_wording') }}
        </ButtonQC>

        <ButtonQC wide data-test="view-plan-continue-button" @click="onProceed">
          {{ $t('travel.common.continue.button') }}
        </ButtonQC>
      </WrapperQC>
    </template>
  </FlowCardQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/mixins' as *;

  .view-plan__info-card {
    border-radius: var(--radius-7);
    border: 1px solid var(--colorBrdrSrfSecondary);
    padding: var(--spacing-7);

    @include mobile-flow {
      padding: var(--spacing-7) var(--spacing-6);
    }

    :deep(.ds-cell__inner) {
      align-items: start;
      gap: var(--spacing-3);

      .ds-cell__value {
        flex-shrink: 0;
      }
    }
  }

  .view-plan__plan-card {
    position: relative;
    border-radius: var(--radius-7);
    border: 1px solid var(--colorBrdrSrfSecondary);
    padding-inline: var(--spacing-7);
    padding-block: var(--spacing-7) var(--spacing-3);

    @include mobile-flow {
      padding-inline: var(--spacing-6);
    }
  }

  .view-plan__plan-card-content {
    color: var(--colorTxtSrfSecondary);

    /* qic-connect-ignore-next-line */
    padding-inline-start: var(--dimension-10);
  }

  .view-plan__discount-price {
    text-decoration: line-through;
    color: var(--colorTxtSrfTertiary);
  }

  .view-plan__plan-card-discount {
    position: absolute;
    display: flex;
    top: var(--dimension--3);
    inset-inline-end: var(--spacing-7);
    width: fit-content;
    padding: var(--spacing-1) var(--spacing-2);
    border-radius: var(--radius-2-5);
    background: var(--colorBgSrfSuccessGreen);
    color: var(--colorTxtSrfSuccessGreen);
  }
</style>

<style lang="scss">
  .view-plan__plan-card-accordion {
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 85%;
      height: 1px;
      background: var(--colorBrdrSrfTertiary);
    }
  }
</style>
